import { useState } from "react";
import { Modal, Descriptions, Tag, Button, Divider, Avatar } from "antd";
import { UserOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons";
import priceFormat from "../../utils/priceFormat";

// Contact Information Popup Component
const ContactInfoModal = ({ open, onClose, agentPhone, agentEmail }) => {
  return (
    <Modal
      title="Agent Contact Information"
      open={open}
      onCancel={onClose}
      width={400}
      centered
      footer={[
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          style={{ backgroundColor: "#b4c540", borderColor: "#b4c540", width: "100%" }}
        >
          Close
        </Button>,
      ]}
    >
      <div className="flex flex-col items-center gap-4 py-4">
        {agentPhone && (
          <div className="flex items-center gap-2 text-[#3687c9] text-lg">
            <PhoneOutlined />
            <span>{agentPhone}</span>
          </div>
        )}
        {agentEmail && (
          <a
            href={`mailto:${agentEmail}`}
            className="flex items-center gap-2 text-[#3687c9] text-lg hover:underline"
          >
            <MailOutlined />
            {agentEmail}
          </a>
        )}
      </div>
    </Modal>
  );
};

const WantsNeedsCard = ({ criteria, open, onClose }) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  if (!criteria) return null;

  // Format price range
  const formatPriceRange = () => {
    if (criteria.minPrice && criteria.maxPrice) {
      return `$${priceFormat(criteria.minPrice)} - $${priceFormat(criteria.maxPrice)}`;
    } else if (criteria.minPrice) {
      return `$${priceFormat(criteria.minPrice)}+`;
    } else if (criteria.maxPrice) {
      return `Up to $${priceFormat(criteria.maxPrice)}`;
    }
    return "-";
  };

  // Format sqft range
  const formatSqftRange = () => {
    if (criteria.minSqFt && criteria.maxSqFt) {
      return `${criteria.minSqFt.toLocaleString()} - ${criteria.maxSqFt.toLocaleString()}`;
    } else if (criteria.minSqFt) {
      return `${criteria.minSqFt.toLocaleString()}+`;
    } else if (criteria.maxSqFt) {
      return `Up to ${criteria.maxSqFt.toLocaleString()}`;
    }
    return "-";
  };

  // Get buyer ID (last 4 characters)
  const buyerId = (criteria.buyerId || criteria._id)?.slice(-4).toUpperCase();

  const handleOpenContactModal = () => {
    setContactModalOpen(true);
  };

  return (
    <>
      <Modal
        title="Wants and Needs"
        open={open}
        onCancel={onClose}
        width={500}
        footer={null}
        className="wants-needs-modal"
      >
        {/* Buyer/Agent Info Header */}
        <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <Avatar size={64} icon={<UserOutlined />} className="bg-gray-300" />
          <div>
            <div className="text-sm">
              <span className="text-gray-700">Buyer ID: </span>
              <span className="text-[#3687c9] font-medium">{buyerId}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-700">Agent: </span>
              <span
                className="text-[#3687c9] font-medium underline cursor-pointer hover:text-[#2a6ba3]"
                onClick={handleOpenContactModal}
              >
                {criteria.agentName || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Criteria Details - matching mobile app layout */}
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label={<span className="text-[#3687c9]">City</span>}>
            {criteria.cities?.join(", ") || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">Zipcode</span>}>
            {criteria.zipCodes?.join(", ") || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">Price</span>}>
            {formatPriceRange()}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">SQFT</span>}>
            {formatSqftRange()}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">Beds</span>}>
            {criteria.minBeds ? `${criteria.minBeds}+` : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">Baths</span>}>
            {criteria.minBath ? `${criteria.minBath}+` : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={<span className="text-[#3687c9]">Notes</span>}>
            {criteria.notes || "-"}
          </Descriptions.Item>
        </Descriptions>

        {/* Amenities */}
        {criteria.amenities && criteria.amenities.length > 0 && (
          <>
            <Divider orientation="left" plain>
              <span className="text-[#3687c9]">Amenities</span>
            </Divider>
            <div className="flex flex-wrap gap-1">
              {criteria.amenities.map((amenity) => (
                <Tag key={amenity._id}>{amenity.title}</Tag>
              ))}
            </div>
          </>
        )}

        {/* Contact Button - Centered */}
        <div className="flex justify-center mt-6">
          <Button
            type="primary"
            onClick={handleOpenContactModal}
            style={{ backgroundColor: "#b4c540", borderColor: "#b4c540" }}
          >
            Contact Buyers Agent
          </Button>
        </div>
      </Modal>

      {/* Contact Information Popup */}
      <ContactInfoModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        agentPhone={criteria.agentPhone}
        agentEmail={criteria.agentEmail}
      />
    </>
  );
};

export default WantsNeedsCard;
